import type { Auth } from 'firebase/auth'
import { gcipAuthTenantIdForSchool, schoolUsesGcipAuthPool } from '@aimsis/shared'
import { api } from '@/lib/api'
import { resolveTenantSlug } from '@/lib/tenant'

/** Raised when a GCIP school cannot resolve `auth.tenantId` (no default-pool fallback). */
export class AuthTenantConfigError extends Error {
  constructor(
    message = 'Sign-in configuration is temporarily unavailable. Wait a moment and try again.',
  ) {
    super(message)
    this.name = 'AuthTenantConfigError'
  }
}

/**
 * Default-pool hosts may sign in when tenant-config is down. GCIP hosts (e.g. demo)
 * must not fall back to the default pool — that routes users to school1 accounts.
 */
const DEFAULT_POOL_FAIL_SOFT_SLUGS = new Set(['school1'])

const CACHE_TTL_MS = 5 * 60_000
/** After a failed fetch, reuse the last successful answer briefly (e.g. 429 under NAT). */
const STALE_ON_ERROR_MS = 60 * 60_000

type CacheEntry = { authTenantId: string | null; expiresAt: number }
const tenantConfigCache = new Map<string, CacheEntry>()

/** Test-only: reset in-memory tenant-config cache. */
export function clearAuthTenantConfigCacheForTests(): void {
  tenantConfigCache.clear()
}

function readFreshCache(slug: string): string | null | undefined {
  const entry = tenantConfigCache.get(slug)
  if (!entry || entry.expiresAt <= Date.now()) return undefined
  return entry.authTenantId
}

function readStaleCacheOnError(slug: string): string | null | undefined {
  const entry = tenantConfigCache.get(slug)
  if (!entry) return undefined
  if (entry.expiresAt + STALE_ON_ERROR_MS <= Date.now()) {
    tenantConfigCache.delete(slug)
    return undefined
  }
  return entry.authTenantId
}

function writeCache(slug: string, authTenantId: string | null): void {
  tenantConfigCache.set(slug, { authTenantId, expiresAt: Date.now() + CACHE_TTL_MS })
}

/** Fetch the Identity Platform tenant id for the current host, if any. */
export async function fetchAuthTenantIdForHost(): Promise<string | null> {
  const slug = resolveTenantSlug()
  if (!slug) return null

  const bakedIn = gcipAuthTenantIdForSchool(slug)
  if (bakedIn) {
    writeCache(slug, bakedIn)
    return bakedIn
  }

  const fresh = readFreshCache(slug)
  if (fresh !== undefined) return fresh

  try {
    const { authTenantId } = await api<{ authTenantId: string | null }>('/v1/auth/tenant-config', {
      unauthenticated: true,
    })
    writeCache(slug, authTenantId)
    return authTenantId
  } catch {
    const stale = readStaleCacheOnError(slug)
    if (stale !== undefined) return stale
    if (DEFAULT_POOL_FAIL_SOFT_SLUGS.has(slug) || !schoolUsesGcipAuthPool(slug)) return null
    throw new AuthTenantConfigError()
  }
}

export function applyFirebaseAuthTenant(auth: Auth, tenantId: string | null): void {
  auth.tenantId = tenantId
}

/** Must run before email/password or custom-token sign-in for tenant schools. */
export async function configureAuthTenantBeforeSignIn(auth: Auth): Promise<void> {
  const tenantId = await fetchAuthTenantIdForHost()
  applyFirebaseAuthTenant(auth, tenantId)
}
